/**
 * GPS WebSocket Factory
 * Factory pattern with dependency injection for GPS WebSocket components
 */

import { ConnectionManager, type ConnectionConfig, type WebSocketDependency } from './ConnectionManager';
import { AuthManager, type AuthConfig } from './AuthManager';
import { EventManager } from './EventManager';
import { RequestManager } from './RequestManager';
import { GpsConnectionStateMachine } from './GpsConnectionStateMachine';
import type { GpsWebSocketSDKOptions } from './types';

/**
 * GPS WebSocket dependencies for testing and flexibility
 */
export interface GpsWebSocketDependencies {
  WebSocket?: WebSocketDependency;
  console?: Console;
  setTimeout?: typeof setTimeout;
  clearTimeout?: typeof clearTimeout;
}

/**
 * Default browser dependencies
 */
const defaultDependencies: Required<GpsWebSocketDependencies> = {
  WebSocket: WebSocket,
  console: console,
  setTimeout: setTimeout,
  clearTimeout: clearTimeout
};

/**
 * GPS WebSocket Factory class
 * Creates GPS WebSocket components with proper dependency injection
 */
export class GpsWebSocketFactory {
  private dependencies: Required<GpsWebSocketDependencies>;

  constructor(dependencies: GpsWebSocketDependencies = {}) {
    this.dependencies = { ...defaultDependencies, ...dependencies };
  }

  /**
   * Create Connection Manager
   */
  createConnectionManager(
    url: string, 
    maxReconnectAttempts: number = 5, 
    reconnectDelay: number = 1000
  ): ConnectionManager {
    const config: ConnectionConfig = {
      url,
      maxReconnectAttempts,
      reconnectDelay
    };

    return new ConnectionManager(config, this.dependencies.WebSocket);
  }

  /**
   * Create Authentication Manager
   */
  createAuthManager(
    baseUrl: string, 
    authMethod: 'query' | 'header' | 'message' = 'query'
  ): AuthManager {
    const config: AuthConfig = {
      baseUrl,
      authMethod
    };

    return new AuthManager(config);
  }

  /**
   * Create Event Manager
   */
  createEventManager(): EventManager {
    return new EventManager();
  }

  /**
   * Create Request Manager
   */
  createRequestManager(defaultTimeout: number = 7000): RequestManager {
    return new RequestManager(defaultTimeout);
  }

  /**
   * Create State Machine
   */
  createStateMachine(): GpsConnectionStateMachine {
    return new GpsConnectionStateMachine();
  }

  /**
   * Create complete GPS WebSocket SDK components
   */
  createComponents(options: GpsWebSocketSDKOptions): {
    connectionManager: ConnectionManager;
    authManager: AuthManager;
    eventManager: EventManager;
    requestManager: RequestManager;
    stateMachine: GpsConnectionStateMachine;
  } {
    const authManager = this.createAuthManager(options.url, options.authMethod);
    const connectionManager = this.createConnectionManager(
      options.url,
      options.maxReconnectAttempts,
      options.reconnectDelay
    );
    const eventManager = this.createEventManager();
    const requestManager = this.createRequestManager();
    const stateMachine = this.createStateMachine();

    return {
      connectionManager,
      authManager,
      eventManager,
      requestManager,
      stateMachine
    };
  }

  /**
   * Update dependencies (useful for testing)
   */
  updateDependencies(newDependencies: Partial<GpsWebSocketDependencies>): void {
    this.dependencies = { ...this.dependencies, ...newDependencies };
  }
}